import { DEFAULT_LANGUAGE } from "./language-preference.ts";
import type { SupportedLanguage } from "./language-preference.ts";

// The language a first visit reads in, before anyone chose one (S10.08): Spanish in a
// Spanish-speaking country, English everywhere else. The country comes from the device's time
// zone, which follows where the person is, and failing that from the region of the browser's
// languages ("es-CL", "en-MX"). Nothing leaves the browser to find it.

/** Countries whose official language is Spanish, by ISO 3166 code. */
const SPANISH_SPEAKING_COUNTRIES = new Set([
  "AR",
  "BO",
  "CL",
  "CO",
  "CR",
  "CU",
  "DO",
  "EC",
  "ES",
  "GQ",
  "GT",
  "HN",
  "MX",
  "NI",
  "PA",
  "PE",
  "PR",
  "PY",
  "SV",
  "UY",
  "VE",
]);

/** The IANA time zones of those countries. */
const SPANISH_SPEAKING_TIME_ZONES = new Set([
  "Europe/Madrid",
  "Africa/Ceuta",
  "Atlantic/Canary",
  "America/Mexico_City",
  "America/Cancun",
  "America/Merida",
  "America/Monterrey",
  "America/Matamoros",
  "America/Chihuahua",
  "America/Ciudad_Juarez",
  "America/Ojinaga",
  "America/Mazatlan",
  "America/Bahia_Banderas",
  "America/Hermosillo",
  "America/Tijuana",
  "America/Buenos_Aires",
  "America/Argentina/Buenos_Aires",
  "America/Argentina/Catamarca",
  "America/Argentina/Cordoba",
  "America/Argentina/Jujuy",
  "America/Argentina/La_Rioja",
  "America/Argentina/Mendoza",
  "America/Argentina/Rio_Gallegos",
  "America/Argentina/Salta",
  "America/Argentina/San_Juan",
  "America/Argentina/San_Luis",
  "America/Argentina/Tucuman",
  "America/Argentina/Ushuaia",
  "America/Cordoba",
  "America/Santiago",
  "America/Punta_Arenas",
  "Pacific/Easter",
  "America/Bogota",
  "America/Lima",
  "America/Caracas",
  "America/Guayaquil",
  "Pacific/Galapagos",
  "America/La_Paz",
  "America/Asuncion",
  "America/Montevideo",
  "America/Costa_Rica",
  "America/Panama",
  "America/Guatemala",
  "America/Tegucigalpa",
  "America/El_Salvador",
  "America/Managua",
  "America/Santo_Domingo",
  "America/Havana",
  "America/Puerto_Rico",
  "Africa/Malabo",
]);

/** What the device says about where it is. */
export type DeviceLocale = {
  readonly timeZone: string | undefined;
  readonly languages: readonly string[];
};

/** Spanish when the device is in a Spanish-speaking country, English otherwise. */
export function languageForLocale(locale: DeviceLocale): SupportedLanguage {
  if (locale.timeZone && SPANISH_SPEAKING_TIME_ZONES.has(locale.timeZone)) return "es";
  // A time zone shared with other countries ("America/New_York") says nothing; the region of
  // the first browser language that names one decides instead.
  const region = locale.languages
    .map((tag) => tag.split("-")[1]?.toUpperCase())
    .find((code) => code !== undefined && code.length === 2);
  if (region) return SPANISH_SPEAKING_COUNTRIES.has(region) ? "es" : "en";
  return DEFAULT_LANGUAGE;
}

/** The device's own locale, read from the browser. */
export function deviceLocale(): DeviceLocale {
  return {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    languages: navigator.languages,
  };
}
